import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Page } from '../../layout/Page';
import { useOrgName, useTerms } from '../../layout';
import { Icon } from '../../design/Icon';
import { fmt } from '../../design/format';
import { Alert, Button, Drawer, EmptyState, ErrorState, Field, Input, MenuItem, Pager, Popover, Switch, Tabs, usePopover, useToast } from '../../components/shared';
import adminAPI, { errorMessage } from '../../services/adminApi';
import { fmtShortDay } from '../calls/callFormat';
import { lower } from './adminModel';
import { CardSearch } from './adminShared';
import { CsvImport } from './CsvImport';
import { ENTRY_TYPES, INTERNAL_KIND_LABEL, entryBody, entryForm, entryTypeLabel, filterEntries, isInternalType, sharedNumberOwners, sortEntries } from './directoryModel';
import { useAdminData } from './useAdminData';
import './admin.css';

const PAGE_SIZE = 50;
const TAB_HASH = { teams: '#teams', import: '#import' };

const tabFromHash = (hash) => Object.keys(TAB_HASH).find((id) => TAB_HASH[id] === hash) || 'list';

/** Directory (mockup 22-admin-directory.html): the people who call, their numbers and community; Teams users read-only; CSV import. */
export function AdminDirectoryPage() {
  const org = useOrgName();
  const terms = useTerms();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, loading, error, retry, reload } = useAdminData(
    {
      entries: adminAPI.getDirectory,
      communities: adminAPI.getCommunities,
      internal: adminAPI.getDirectoryInternal,
    },
    { optional: ['internal'] }
  );
  const [tab, setTab] = useState(tabFromHash(location.hash));
  const [chooser, setChooser] = useState(0);
  const [editing, setEditing] = useState(null);

  const showTab = (id) => {
    setTab(id);
    navigate({ hash: TAB_HASH[id] || '' }, { replace: true });
  };

  const entries = useMemo(() => sortEntries(data?.entries), [data]);
  const communities = useMemo(() => (data?.communities || []).filter((c) => c.isActive !== false).sort((a, b) => a.name.localeCompare(b.name)), [data]);
  const communityName = useMemo(() => new Map((data?.communities || []).map((c) => [c.id, c.name])), [data]);
  const internal = data?.internal || [];

  const exportColumns = [
    { key: 'fullName', header: 'Name' },
    { key: 'entryType', header: 'Type', value: (e) => entryTypeLabel(e.entryType) },
    { key: 'community', header: terms.community, value: (e) => (e.communityId ? communityName.get(e.communityId) || '' : `All ${terms.communitiesLower}`) },
    { key: 'unitOrLot', header: 'Unit or lot' },
    { key: 'phoneNumbers', header: 'Phone numbers', value: (e) => (e.phoneNumbers || []).join('; ') },
    { key: 'emails', header: 'Emails', value: (e) => (e.emails || []).join('; ') },
    { key: 'streetAddress', header: 'Street address' },
    { key: 'active', header: 'Active', value: (e) => (e.isActive === false ? 'No' : 'Yes') },
  ];

  const openImport = () => {
    showTab('import');
    setChooser((n) => n + 1);
  };

  const actions = (
    <>
      <Button onClick={() => setEditing({})} disabled={!data}>
        <Icon name="plus" className="ico-sm" />
        Add person
      </Button>
      <Button variant="primary" onClick={openImport}>
        <Icon name="upload" className="ico-sm" />
        Import CSV
      </Button>
    </>
  );

  const subtitle = `The people who call ${org}. Calls from a listed number are tied to that person's ${terms.communityLower} and show who called.`;
  const page = (children) => (
    <Page title="Directory" subtitle={subtitle} actions={actions} exportConfig={{ name: 'Directory', columns: exportColumns, rows: entries }}>
      {children}
    </Page>
  );

  if (error) return page(<ErrorState title="The directory couldn't load" body="Vertolytics couldn't read the caller directory. Nothing was changed." error={error} onRetry={retry} />);

  const tabs = (
    <Tabs
      label="Directory"
      value={tab}
      onChange={showTab}
      tabs={[
        { id: 'list', label: 'People', count: data ? fmt.int(entries.length) : undefined },
        { id: 'teams', label: 'Teams users', count: data && data.internal ? fmt.int(internal.length) : undefined },
        { id: 'import', label: 'Import from CSV' },
      ]}
    />
  );

  if (loading || !data) {
    return page(
      <>
        {tabs}
        <section className="card is-loading">
          <div className="card-body">
            <div style={{ height: 420 }} />
          </div>
        </section>
      </>
    );
  }

  const drawer = (
    <EntryDrawer
      entry={editing}
      communities={communities}
      onClose={() => setEditing(null)}
      onDone={async (message) => {
        setEditing(null);
        await reload();
        toast(message);
      }}
    />
  );

  const importer = (
    <CsvImport
      only="directory"
      visible={tab === 'import'}
      orgName={org}
      openChooser={chooser}
      onPreview={() => showTab('import')}
      onApplied={async () => {
        await reload();
        showTab('list');
      }}
    />
  );

  if (tab === 'list' && entries.length === 0) {
    return page(
      <>
        <EmptyState
          icon="user"
          title="No one in the directory yet"
          body={`Add people one at a time, import ${org}'s owner list from a CSV, or save a caller's number from a call.`}
          actions={
            <>
              <Button onClick={() => setEditing({})}>Add person</Button>
              <Button variant="primary" onClick={openImport}>
                Import CSV
              </Button>
            </>
          }
        />
        {importer}
        {drawer}
      </>
    );
  }

  return page(
    <>
      {tabs}
      {tab === 'list' && <PeopleList entries={entries} communities={communities} communityName={communityName} onEdit={setEditing} />}
      {tab === 'teams' && <TeamsUsers people={data.internal} />}
      {importer}
      {drawer}
    </>
  );
}

function FilterChip({ label, value, options, onChange, allLabel }) {
  const pop = usePopover();
  const current = options.find((o) => o.id === value);
  return (
    <Popover
      open={pop.open}
      onClose={pop.close}
      align="right"
      width={240}
      label={`Filter by ${lower(label)}`}
      anchor={
        <button type="button" className={`chip${value ? ' active' : ''}`} onClick={pop.toggle} aria-haspopup="true" aria-expanded={pop.open}>
          <span className="k">{label}</span>
          {current ? current.label : 'All'}
          <Icon name="chevD" className="ico-sm" />
        </button>
      }
    >
      {[{ id: null, label: allLabel }, ...options].map((o) => (
        <MenuItem
          key={o.id || 'all'}
          on={value === o.id}
          checkable
          onClick={() => {
            onChange(o.id);
            pop.close();
          }}
        >
          {o.label}
        </MenuItem>
      ))}
    </Popover>
  );
}

function PeopleList({ entries, communities, communityName, onEdit }) {
  const terms = useTerms();
  const [query, setQuery] = useState('');
  const [communityId, setCommunityId] = useState(null);
  const [type, setType] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const shared = useMemo(() => sharedNumberOwners(entries), [entries]);
  const visible = filterEntries(entries, { query, communityId, type });
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const current = Math.min(pageNumber, pageCount);
  const rows = visible.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  useEffect(() => setPageNumber(1), [query, communityId, type]);

  const shown = visible.length ? `Showing ${fmt.int((current - 1) * PAGE_SIZE + 1)}–${fmt.int((current - 1) * PAGE_SIZE + rows.length)} of ${fmt.int(visible.length)}.` : 'No one matches.';

  return (
    <section className="card">
      <div className="card-head" style={{ alignItems: 'center' }}>
        <CardSearch value={query} onChange={setQuery} placeholder="Search names, numbers and emails" />
        <div className="acts">
          <FilterChip label={terms.community} value={communityId} onChange={setCommunityId} allLabel={`All ${terms.communitiesLower}`} options={communities.map((c) => ({ id: c.id, label: c.name }))} />
          <FilterChip label="Type" value={type} onChange={setType} allLabel="All types" options={ENTRY_TYPES} />
        </div>
      </div>
      <div className="card-body flush">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>{terms.community}</th>
                <th>Unit or lot</th>
                <th>Phone numbers</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id}>
                  <td className="cell-title">
                    {e.fullName}
                    {e.isActive === false && (
                      <>
                        {' '}
                        <span className="badge">Inactive</span>
                      </>
                    )}
                  </td>
                  <td>{isInternalType(e.entryType) ? <span className="badge b-info">{entryTypeLabel(e.entryType)}</span> : <span className="small">{entryTypeLabel(e.entryType)}</span>}</td>
                  <td>{e.communityId ? communityName.get(e.communityId) || <span className="muted">Unknown {terms.communityLower}</span> : <span className="muted">All {terms.communitiesLower}</span>}</td>
                  <td>{e.unitOrLot || <span className="muted">—</span>}</td>
                  <td className="num">
                    {(e.phoneNumbers || []).map((p, i) => (
                      <React.Fragment key={`${p}-${i}`}>
                        {i > 0 && <br />}
                        {p}
                      </React.Fragment>
                    ))}
                    {shared.has(e.id) && (
                      <>
                        {' '}
                        <span className="badge b-warning" title={`Also listed for ${shared.get(e.id)} in another ${terms.communityLower}, so calls from it aren't matched`}>
                          <Icon name="alert" />
                          Shared
                        </span>
                      </>
                    )}
                  </td>
                  <td className="small">{e.updatedAt ? fmtShortDay(e.updatedAt) : ''}</td>
                  <td>
                    <button className="btn btn-ghost btn-sm" type="button" onClick={() => onEdit(e)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="table-foot">
        <span className="caption">{shown} Staff and vendors are labelled on their calls and aren't counted as residents.</span>
        {pageCount > 1 && <Pager page={current} pageCount={pageCount} onChange={setPageNumber} />}
      </div>
    </section>
  );
}

function TeamsUsers({ people }) {
  return (
    <div className="stack">
      <div className="alert">
        <Icon name="info" />
        <div>Teams users come from Microsoft Teams and can't be edited here. Calls between them are Teams calls.</div>
      </div>
      <section className="card">
        <div className="card-body flush">
          {people ? (
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Known as</th>
                </tr>
              </thead>
              <tbody>
                {people.map((u) => (
                  <tr key={u.entraId}>
                    <td className="cell-title">{u.displayName}</td>
                    <td className="small">{u.email}</td>
                    <td className="small">{INTERNAL_KIND_LABEL[u.kind] || u.kind}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="caption" style={{ padding: 16 }}>
              Teams users couldn't load. Reload the page to try again.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

function EntryDrawer({ entry, communities, onClose, onDone }) {
  const isNew = entry && !entry.id;
  const terms = useTerms();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    if (!entry) return;
    setProblem(null);
    setForm(entryForm(entry, communities[0]?.id || ''));
  }, [entry, communities]);

  if (!entry || !form) return null;
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const run = async (work, message) => {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      await onDone(message);
    } catch (err) {
      setProblem(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const body = entryBody(form, entry);
  const save = () => run(() => (isNew ? adminAPI.createDirectoryEntry(body) : adminAPI.updateDirectoryEntry(body)), isNew ? `Added ${body.fullName}` : `Saved ${body.fullName}`);
  const remove = () => run(() => adminAPI.deleteDirectoryEntry(entry.id), `Deleted ${entry.fullName}`);

  return (
    <Drawer
      open
      onClose={onClose}
      title={isNew ? 'Add person' : `Edit ${entry.fullName}`}
      footer={
        <>
          {!isNew && (
            <button className="btn btn-ghost" type="button" style={{ marginRight: 'auto', color: 'var(--critical)' }} onClick={remove} disabled={busy}>
              <Icon name="trash" className="ico-sm" />
              Delete
            </button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={busy || !form.fullName.trim()}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Name" htmlFor="de-n">
          <Input id="de-n" value={form.fullName} onChange={set('fullName')} autoFocus />
        </Field>
        <div className="form-grid">
          <Field label="Type" htmlFor="de-t">
            <select className="select" id="de-t" value={form.entryType} onChange={set('entryType')}>
              {ENTRY_TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label={terms.community} htmlFor="de-c">
            <select className="select" id="de-c" value={form.communityId} onChange={set('communityId')}>
              {communities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
              <option value="">All {terms.communitiesLower}</option>
            </select>
          </Field>
        </div>
        <Field label="Phone numbers" htmlFor="de-p" hint="One per line. Any format; Vertolytics compares the digits.">
          <textarea className="input" id="de-p" rows={2} style={{ height: 'auto' }} value={form.phoneNumbers} onChange={set('phoneNumbers')} />
        </Field>
        <div className="form-grid">
          <Field label="Unit or lot" htmlFor="de-l">
            <Input id="de-l" value={form.unitOrLot} onChange={set('unitOrLot')} />
          </Field>
          <Field label="Email" htmlFor="de-e">
            <Input id="de-e" value={form.emails} onChange={set('emails')} />
          </Field>
        </div>
        <Field label="Street address" htmlFor="de-a">
          <Input id="de-a" value={form.streetAddress} onChange={set('streetAddress')} />
        </Field>
        <Switch label="Active" checked={form.isActive} onChange={set('isActive')} />
        {problem && <Alert tone="error">{problem}</Alert>}
      </div>
    </Drawer>
  );
}
